export interface ParsedCsvChoice {
  value: string;
  label: string;
  filter?: string;
}

export type CsvParseResult = { ok: true; choices: ParsedCsvChoice[] } | { ok: false; error: string };

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

// FileReader (em vez de File#text()/arrayBuffer()) mantém compatibilidade ampla com
// navegadores e com o polyfill de File do jsdom usado nos testes.
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler o arquivo.'));
    reader.readAsText(file, 'utf-8');
  });
}

/**
 * Spec 033: parser client-side do CSV de opções eleitorais (Valor, Rótulo/Descrição, Filtro).
 * Separador vírgula, arquivo UTF-8, primeira linha é cabeçalho e é ignorada. Rejeita o
 * arquivo inteiro em caso de estrutura inválida ou valores duplicados na Coluna A.
 */
export async function parseElectoralOptionsCsv(file: File): Promise<CsvParseResult> {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { ok: false, error: 'Arquivo maior que 5 MB. Reduza o arquivo e tente novamente.' };
  }

  const text = await readFileAsText(file);
  const lines = text.split(/\r\n|\r|\n/).filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    return { ok: false, error: 'Arquivo CSV vazio.' };
  }

  const dataLines = lines.slice(1);
  if (dataLines.length === 0) {
    return { ok: false, error: 'O arquivo CSV não possui linhas de dados além do cabeçalho.' };
  }

  const choices: ParsedCsvChoice[] = [];
  const lineNumbersByValue = new Map<string, number[]>();

  for (let index = 0; index < dataLines.length; index++) {
    const lineNumber = index + 2; // +1 para índice 1-based, +1 pela linha de cabeçalho
    const columns = dataLines[index].split(',');

    if (columns.length !== 3) {
      return {
        ok: false,
        error: `Linha ${lineNumber}: esperado exatamente 3 colunas (Valor, Rótulo/Descrição, Filtro), encontrado ${columns.length}.`,
      };
    }

    const value = columns[0].trim();
    const label = columns[1].trim();
    const filter = columns[2].trim();

    if (!value || !label) {
      return { ok: false, error: `Linha ${lineNumber}: Valor e Rótulo/Descrição são obrigatórios.` };
    }

    lineNumbersByValue.set(value, [...(lineNumbersByValue.get(value) ?? []), lineNumber]);
    choices.push(filter ? { value, label, filter } : { value, label });
  }

  const duplicates = Array.from(lineNumbersByValue.entries()).filter(([, lineNumbers]) => lineNumbers.length > 1);
  if (duplicates.length > 0) {
    const description = duplicates
      .map(([value, lineNumbers]) => `"${value}" (linhas ${lineNumbers.join(', ')})`)
      .join('; ');
    return { ok: false, error: `Valores duplicados na Coluna A: ${description}.` };
  }

  return { ok: true, choices };
}
